import type { Unit } from './types';

// Ítems de la inspección físico-mecánica (walkaround) — copia de
// firebase/functions/src/inspectionItems.mjs; mantener ambas al día.
// applies: 'motor' = vehículos; 'trailer' = remolcados; 'both' = cualquiera;
// 'reefer' = solo si la unidad lleva componente (equipo de frío).

// El estado por ítem vive en types.ts (el contrato del servicio lo usa ahí);
// se reexporta para que los imports existentes sigan funcionando.
export type { InspectionItemStatus } from './types';
export type InspectionApplies = 'motor' | 'trailer' | 'both' | 'reefer';
export interface InspectionGroup {
  key: string; label: string; applies: InspectionApplies;
  items: { key: string; label: string }[];
}

export const INSPECTION_GROUPS: InspectionGroup[] = [
  { key: 'cab', label: 'Cabina y controles', applies: 'motor', items: [
    { key: 'cab_gauges', label: 'Indicadores, velocímetro y presión de aire' },
    { key: 'cab_horn', label: 'Claxon' },
    { key: 'cab_wipers', label: 'Limpiaparabrisas' },
    { key: 'cab_glass', label: 'Parabrisas sin grietas ni obstrucciones' },
    { key: 'cab_mirrors', label: 'Espejos' },
    { key: 'cab_belt', label: 'Cinturón de seguridad' },
    { key: 'cab_heat', label: 'Calefacción y desempañador' },
  ]},
  { key: 'engine', label: 'Motor y compartimento', applies: 'motor', items: [
    { key: 'eng_oil', label: 'Nivel de aceite' },
    { key: 'eng_coolant', label: 'Nivel de anticongelante' },
    { key: 'eng_belts', label: 'Bandas y mangueras' },
    { key: 'eng_leaks', label: 'Sin fugas (aceite, diesel, aire, anticongelante)' },
    { key: 'eng_exhaust', label: 'Escape y tanque de diesel sin fugas' },
    { key: 'eng_battery', label: 'Batería y marcha' },
  ]},
  { key: 'lights', label: 'Luces y reflejantes', applies: 'both', items: [
    { key: 'light_head', label: 'Faros altos y bajos' },
    { key: 'light_turn', label: 'Direccionales' },
    { key: 'light_marker', label: 'Cuartos y luces marcadoras' },
    { key: 'light_brake', label: 'Luces de freno y reversa' },
    { key: 'light_flash', label: 'Intermitentes de emergencia' },
    { key: 'light_reflect', label: 'Reflejantes y cinta de conspicuidad' },
  ]},
  { key: 'tires', label: 'Llantas y rines', applies: 'both', items: [
    { key: 'tire_press', label: 'Presión de inflado' },
    { key: 'tire_tread', label: 'Profundidad de dibujo (direccional 4/32", resto 2/32")' },
    { key: 'tire_damage', label: 'Sin cortaduras, bultos ni desgaste irregular' },
    { key: 'tire_lugs', label: 'Birlos completos y apretados' },
    { key: 'tire_rims', label: 'Rines sin grietas ni reparaciones' },
    { key: 'tire_hubs', label: 'Sellos de cubeta sin fugas' },
  ]},
  { key: 'brakes', label: 'Frenos', applies: 'both', items: [
    { key: 'brk_service', label: 'Freno de servicio' },
    { key: 'brk_parking', label: 'Freno de estacionamiento' },
    { key: 'brk_lines', label: 'Líneas y mangueras de aire' },
    { key: 'brk_airtest', label: 'Prueba de aire (gobernador ~100 psi, sin caída aplicada)' },
    { key: 'brk_slack', label: 'Ajustadores (slack) dentro de su recorrido' },
    { key: 'brk_drum', label: 'Tambores y balatas (≥1/4")' },
    { key: 'brk_alarm', label: 'Alarma de baja presión de aire' },
  ]},
  { key: 'steering', label: 'Dirección y suspensión', applies: 'motor', items: [
    { key: 'str_play', label: 'Juego de dirección, brazo pitman y tirantes' },
    { key: 'sus_springs', label: 'Ballestas o bolsas de aire y amortiguadores' },
    { key: 'sus_bush', label: 'Bujes y soportes' },
  ]},
  { key: 'coupling', label: 'Acople y quinta rueda', applies: 'motor', items: [
    { key: 'cpl_plate', label: 'Plato de quinta (grasa y montaje)' },
    { key: 'cpl_jaws', label: 'Mordazas con seguro; apron sin deformación' },
    { key: 'cpl_arm', label: 'Brazo de liberación' },
    { key: 'cpl_glad', label: 'Gladhands y líneas de aire (servicio/emergencia)' },
    { key: 'cpl_pigtail', label: 'Cable eléctrico de 7 vías' },
    { key: 'cpl_kingpin', label: 'Kingpin de la caja (recto, sin grietas)' },
  ]},
  { key: 'trailer', label: 'Caja y estructura', applies: 'trailer', items: [
    { key: 'trl_doors', label: 'Puertas, bisagras y seguros' },
    { key: 'trl_frame', label: 'Chasis, travesaños y paredes' },
    { key: 'trl_landing', label: 'Tren de aterrizaje (landing gear)' },
    { key: 'trl_flaps', label: 'Salpicaderas' },
    { key: 'trl_susp', label: 'Suspensión (muelles o bolsas de aire)' },
    { key: 'trl_floor', label: 'Piso interior y techo' },
    { key: 'trl_guard', label: 'Defensa trasera (underride)' },
  ]},
  { key: 'cargo', label: 'Sujeción de carga', applies: 'both', items: [
    { key: 'crg_binder', label: 'Cadenas, tirantes y gatas' },
    { key: 'crg_tarp', label: 'Lonas y amarres' },
    { key: 'crg_load', label: 'Estiba asegurada y repartida' },
  ]},
  { key: 'reefer', label: 'Equipo de reefer', applies: 'reefer', items: [
    { key: 'rf_fuel', label: 'Diesel del equipo' },
    { key: 'rf_belt', label: 'Banda y condensador' },
    { key: 'rf_coolant', label: 'Anticongelante del equipo' },
    { key: 'rf_set', label: 'Setpoint y temperatura operando' },
    { key: 'rf_doors', label: 'Sellos de puertas y cortinas' },
  ]},
  { key: 'safety', label: 'Seguridad y documentos', applies: 'both', items: [
    { key: 'saf_ext', label: 'Extintor cargado, marcado, montado y accesible' },
    { key: 'saf_tri', label: 'Triángulos reflejantes bidireccionales (3)' },
    { key: 'saf_flare', label: 'Lámparas de emergencia (bengalas solo si la carga no es inflamable)' },
    { key: 'saf_fuse', label: 'Fusibles de repuesto (uno por tipo)' },
    { key: 'saf_kit', label: 'Botiquín (recomendado)' },
    { key: 'saf_spare', label: 'Llanta de refacción y herramientas (recomendado)' },
    { key: 'saf_docs', label: 'Licencia federal, tarjeta de circulación y póliza' },
    { key: 'saf_permits', label: 'Permisos SCT / CAAT vigentes' },
    { key: 'saf_us', label: 'Docs de cruce EUA: DOT visible, authority e inspección anual' },
  ]},
];

export const INSPECTION_ITEM_LABEL: Record<string, string> = Object.fromEntries(
  INSPECTION_GROUPS.flatMap(g => g.items.map(i => [i.key, i.label])),
);

export const INSPECTION_KIND_LABEL: Record<string, string> = { pretrip: 'Pre-viaje', posttrip: 'Post-viaje' };

/** Grupos que aplican a la unidad: cabina/motor/acople solo en vehículos,
    caja solo en remolcados, reefer solo si lleva componente de frío. */
export function inspectionGroupsFor(unit: Unit): InspectionGroup[] {
  const isMotor = unit.category === 'vehicle';
  const isTrailer = unit.category === 'attachment';
  const hasReefer = unit.components.length > 0;
  return INSPECTION_GROUPS.filter(g =>
    g.applies === 'both' ||
    (g.applies === 'motor' && isMotor) ||
    (g.applies === 'trailer' && isTrailer) ||
    (g.applies === 'reefer' && hasReefer));
}
